import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Popover,
  Badge,
  Switch,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, CircleMarker } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { apiService } from "../../services/api";
import { WL_RED, CARD_BORDER } from "../../utils/designTokens";

// Add CSS for weather icons to ensure proper styling
const weatherIconStyles = `
  .custom-weather-icon {
    background: transparent !important;
    border: none !important;
  }
  .weather-icon-high {
    background-color: #FEF2F2 !important;
    border: 2px solid #F87171 !important;
    color: #DC2626 !important;
  }
  .weather-icon-low {
    background-color: #F0FDF4 !important;
    border: 2px solid #BBF7D0 !important;
    color: #16A34A !important;
  }
`;

const FONT = "'Inter', sans-serif";

interface WeatherAlert {
  id: string;
  location: string;
  lat: number;
  lng: number;
  severity: "high" | "low";
  weather_description: string;
  temperature_f: number | null;
  wind_speed_mph: number | null;
  wind_gust_mph: number | null;
  rain_mm: number | null;
  snowfall_cm: number | null;
}

interface PortWatchPoint {
  lat: number;
  lng: number;
  location: string;
  reason_short: string;
  issue_type?: string;
  trend: "drop" | "increase";
  change_pct: number;
  drop_pct: number;
  current_avg_calls: number;
  baseline_avg_calls: number;
  latest_calls?: number;
  latest_date_ms: number;
  lookback_days: number;
  baseline_days: number;
  portid: string;
}

type PortWatchStyle = { border: string; bg: string };

/** Merge GeoJSON-style `properties` so we read the same fields from any API shape. */
function flattenPortWatchRaw(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== "object") return {};
  const r = raw as Record<string, unknown>;
  const nested = r.properties;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...r, ...(nested as Record<string, unknown>) };
  }
  return { ...r };
}

function pickFirst<T>(obj: Record<string, unknown>, keys: string[]): T | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "") return v as T;
  }
  return undefined;
}

function shortReasonFromChange(changePct: number, trend: "drop" | "increase"): string {
  if (trend === "increase") {
    if (changePct >= 40) return "Severe port call surge";
    if (changePct >= 25) return "Major port call surge";
    if (changePct >= 10) return "Notable port call increase";
    return "Slight port call increase";
  }
  if (changePct >= 40) return "Severe port call drop";
  if (changePct >= 25) return "Major port call drop";
  if (changePct >= 10) return "Notable port call drop";
  return "Slight port call drop";
}

/**
 * Normalize PortWatch rows from snake_case, camelCase, or legacy dashboard payloads
 * so map tooltips always get location, delay text, and metrics.
 */
function normalizePortWatchPoint(raw: unknown): PortWatchPoint | null {
  const o = flattenPortWatchRaw(raw);
  const lat = Number(pickFirst(o, ["lat", "latitude"]));
  const lng = Number(pickFirst(o, ["lng", "lon", "longitude"]));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const normalizeTrendAndChange = (
    data: Record<string, unknown>,
  ): { changePct: number; trend: "drop" | "increase" } => {
    let localChangePct = Number(pickFirst(data, ["change_pct", "changePct"]));
    let localTrend: "drop" | "increase" = "drop";
    const trendValue = String(pickFirst(data, ["trend"]) ?? "").trim().toLowerCase();
    if (trendValue === "increase") {
      localTrend = "increase";
    }
    if (!Number.isFinite(localChangePct)) {
      const dropPct = Number(pickFirst(data, ["drop_pct", "dropPct"]));
      if (Number.isFinite(dropPct)) {
        localChangePct = Math.abs(dropPct);
        localTrend = "drop";
      } else {
        localChangePct = 0;
        localTrend = "drop";
      }
    }
    return { changePct: Math.max(0, localChangePct), trend: localTrend };
  };
  const { changePct, trend } = normalizeTrendAndChange(o);

  const lookback_days = Number(pickFirst(o, ["lookback_days", "lookbackDays"])) || 30;
  const baseline_days = Number(pickFirst(o, ["baseline_days", "baselineDays"])) || 90;

  let current_avg_calls = Number(pickFirst(o, ["current_avg_calls", "currentAvgCalls"]));
  let baseline_avg_calls = Number(pickFirst(o, ["baseline_avg_calls", "baselineAvgCalls"]));
  if (!Number.isFinite(current_avg_calls)) current_avg_calls = 0;
  if (!Number.isFinite(baseline_avg_calls)) baseline_avg_calls = current_avg_calls;

  let location = String(pickFirst(o, ["location", "Location"]) ?? "").trim();
  if (!location) {
    location = String(pickFirst(o, ["port_name", "portName", "portname"]) ?? "").trim();
  }
  if (!location) {
    const title = String(pickFirst(o, ["issue_title", "issueTitle"]) ?? "").trim();
    location = title.replace(/-\d{2,4}$/, "").trim() || "Maritime chokepoint";
  }

  let reason_short = String(pickFirst(o, ["reason_short", "reasonShort"]) ?? "").trim();
  if (!reason_short) {
    reason_short = String(pickFirst(o, ["reason"]) ?? "").trim();
  }
  if (!reason_short) {
    reason_short = shortReasonFromChange(changePct, trend);
  }

  let latest_date_ms = Number(pickFirst(o, ["latest_date_ms", "latestDateMs"]));
  if (!Number.isFinite(latest_date_ms)) {
    latest_date_ms = Number(pickFirst(o, ["fromdate_ms", "fromDateMs"]));
  }
  if (!Number.isFinite(latest_date_ms)) {
    latest_date_ms = Date.now();
  }

  const latestRaw = Number(pickFirst(o, ["latest_calls", "latestCalls"]));

  return {
    lat,
    lng,
    location,
    reason_short,
    trend,
    change_pct: changePct,
    issue_type: String(pickFirst(o, ["issue_type", "issueType"]) ?? "congestion"),
    drop_pct: trend === "drop" ? changePct : 0,
    current_avg_calls,
    baseline_avg_calls,
    latest_calls: Number.isFinite(latestRaw) ? latestRaw : undefined,
    latest_date_ms,
    lookback_days,
    baseline_days,
    portid: String(pickFirst(o, ["portid", "portId"]) ?? ""),
  };
}

function extractPortWatchPoints(payload: unknown): PortWatchPoint[] {
  if (!payload || typeof payload !== "object") return [];
  const body = payload as Record<string, unknown>;
  const nested = body.data && typeof body.data === "object" ? (body.data as Record<string, unknown>) : null;
  const rawList =
    (Array.isArray(body.points) ? body.points : undefined) ??
    (nested && Array.isArray(nested.points) ? nested.points : undefined) ??
    [];
  const out: PortWatchPoint[] = [];
  for (const item of rawList) {
    const n = normalizePortWatchPoint(item);
    if (n) out.push(n);
  }
  return out;
}

/** PortWatch severity from % drop in ship visits (aligns with backend tiers). */
type PortWatchBand = "critical" | "high" | "moderate" | "watch";

function portWatchBand(changePct: number): PortWatchBand {
  if (changePct >= 40) return "critical";
  if (changePct >= 25) return "high";
  if (changePct >= 10) return "moderate";
  return "watch";
}

const PORT_WATCH_STYLE: Record<"drop" | "increase", Record<PortWatchBand, { border: string; bg: string }>> = {
  drop: {
    critical: { border: "#DC2626", bg: "#FEE2E2" },
    high: { border: "#DC2626", bg: "#FEE2E2" },
    moderate: { border: "#DC2626", bg: "#FEE2E2" },
    watch: { border: "#DC2626", bg: "#FEE2E2" },
  },
  increase: {
    critical: { border: "#1D4ED8", bg: "#DBEAFE" },
    high: { border: "#1D4ED8", bg: "#DBEAFE" },
    moderate: { border: "#1D4ED8", bg: "#DBEAFE" },
    watch: { border: "#1D4ED8", bg: "#DBEAFE" },
  },
};

/** ~2.5 km — if weather shares a cell with PortWatch, nudge the weather pin so both stay clickable. */
const COLLISION_EPS_DEG = 0.022;

function nudgedWeatherLatLng(
  lat: number,
  lng: number,
  portPoints: PortWatchPoint[],
  salt: number,
): [number, number] {
  const collides = portPoints.some(
    (p) => Math.abs(p.lat - lat) < COLLISION_EPS_DEG && Math.abs(p.lng - lng) < COLLISION_EPS_DEG,
  );
  if (!collides) return [lat, lng];
  const step = 0.014;
  const a = (salt % 360) * (Math.PI / 180);
  return [lat + Math.cos(a) * step, lng + Math.sin(a) * step];
}

/** Same map data often arrives as a new array reference; treat as unchanged so markers are not refreshed (Leaflet closes tooltips on marker moves). */
function portWatchRadius(changePct: number): number {
  if (changePct >= 40) return 14;  // Critical - largest
  if (changePct >= 25) return 10;  // High - medium  
  if (changePct >= 10) return 7;   // Moderate - smallest
  return 5; // Below threshold - very small
}

// Define major ports with persistent labels
const MAJOR_PORTS = [
  "Shanghai", "Hamburg", "Oakland", "Seattle", 
  "Los Angeles", "Long Beach", "New York/Newark", 
  "Tokyo", "Busan", "Shenzhen"
];

// Create dimmed weather icons for non-severe weather
const createWeatherIcon = (severity: "high" | "low") => {
  const isHighSeverity = severity === "high";
  const iconHtml = `
    <div class="weather-icon-${severity}" style="
      background-color: ${isHighSeverity ? '#FEF2F2' : '#F0FDF4'} !important;
      border: 2px solid ${isHighSeverity ? '#F87171' : '#BBF7D0'} !important;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      color: ${isHighSeverity ? '#DC2626' : '#16A34A'} !important;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      font-weight: bold;
    ">
      ${isHighSeverity ? '⚠' : '☁'}
    </div>
  `;
  
  return L.divIcon({
    html: iconHtml,
    className: 'custom-weather-icon',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const WEATHER_MARK_ICONS = {
  high: createWeatherIcon("high"),
  low: createWeatherIcon("low")
};

const PortWatchCircleLayer = function PortWatchCircleLayerFn({ points }: { points: PortWatchPoint[] }) {
  const [clusterGroup, setClusterGroup] = useState<any>(null);
  // Create a stable key that changes when data changes
  const dataKey = useMemo(() => {
    return points.map(p => `${p.portid}-${p.trend}-${p.change_pct}`).join('|');
  }, [points]);
  
  // Separate major ports for persistent labels
  const majorPorts = points.filter(pt => 
    MAJOR_PORTS.some(major => pt.location.toLowerCase().includes(major.toLowerCase()))
  );
  const otherPorts = points.filter(pt => 
    !MAJOR_PORTS.some(major => pt.location.toLowerCase().includes(major.toLowerCase()))
  );

  const renderPortWatchTooltip = (pt: PortWatchPoint, st: PortWatchStyle) => (
    <div
      style={{
        fontFamily: FONT,
        width: "220px",
        maxWidth: "calc(100vw - 120px)",
        padding: "8px 12px",
        boxSizing: "border-box",
        wordWrap: "break-word",
        overflowWrap: "anywhere",
        hyphens: "auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
        <span
          style={{
            fontWeight: 700,
            fontSize: "0.86rem",
            color: "#111827",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            minWidth: 0,
            flex: 1,
          }}
        >
          {pt.location}
        </span>
        <span
          style={{
            marginLeft: 8,
            padding: "2px 8px",
            borderRadius: 20,
            fontSize: "0.6875rem",
            fontWeight: 700,
            background: st.bg,
            color: st.border,
            border: `1px solid ${st.border}`,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {pt.trend === "drop" ? "↓" : "↑"} {pt.change_pct}%
        </span>
      </div>
      <div style={{ fontSize: "0.8125rem", color: "#374151", marginBottom: 4 }}>
        {shortReasonFromChange(pt.change_pct, pt.trend)}
      </div>
    </div>
  );
  const portMetaByCoord = useMemo(() => {
    const lookup = new Map<string, { trend: "drop" | "increase"; band: PortWatchBand }>();
    for (const pt of otherPorts) {
      const key = `${pt.lat.toFixed(5)}:${pt.lng.toFixed(5)}`;
      lookup.set(key, { trend: pt.trend, band: portWatchBand(pt.change_pct) });
    }
    return lookup;
  }, [otherPorts]);

  useEffect(() => {
    if (clusterGroup) {
      const createClusterIcon = (cluster: any) => {
        const count = cluster.getChildCount();
        let dropCount = 0;
        let increaseCount = 0;
        const children = typeof cluster.getAllChildMarkers === "function"
          ? cluster.getAllChildMarkers()
          : [];
        for (const child of children) {
          const latlng = child?.getLatLng?.();
          if (!latlng) continue;
          const key = `${Number(latlng.lat).toFixed(5)}:${Number(latlng.lng).toFixed(5)}`;
          const meta = portMetaByCoord.get(key);
          if (!meta) continue;
          if (meta.trend === "drop") dropCount += 1;
          else increaseCount += 1;
        }

        let size: "small" | "medium" | "large" = "small";
        if (count > 10) {
          size = "large";
        } else if (count > 3) {
          size = "medium";
        }
        const dominantTrend = dropCount >= increaseCount ? "drop" : "increase";
        const bgColor = dominantTrend === "drop" ? "#DC2626" : "#1D4ED8";
        const sizeMap: Record<typeof size, number> = { small: 22, medium: 30, large: 36 };
        const fontSizeBySize: Record<typeof size, string> = {
          small: "11px",
          medium: "12px",
          large: "13px",
        };

        return L.divIcon({
          html: `<div style="
              background-color: ${bgColor};
              color: white;
              border-radius: 50%;
              width: ${sizeMap[size]}px;
              height: ${sizeMap[size]}px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              font-size: ${fontSizeBySize[size]};
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              border: 2px solid white;
            " title="Affected ports: ${String(count)}">${String(count)}</div>`,
          className: 'custom-cluster-icon',
          iconSize: [sizeMap[size], sizeMap[size]]
        });
      };

      // Stricter clustering: smaller radius + earlier declustering on zoom-in.
      clusterGroup.options.maxClusterRadius = 6;
      clusterGroup.options.disableClusteringAtZoom = 6;
      clusterGroup.options.spiderfyOnMaxZoom = true;
      clusterGroup.options.showCoverageOnHover = false;
      clusterGroup.options.zoomToBoundsOnClick = true;
      clusterGroup.options.iconCreateFunction = createClusterIcon;

      if (typeof clusterGroup.refreshClusters === "function") {
        clusterGroup.refreshClusters();
      }
    }
  }, [clusterGroup, dataKey, portMetaByCoord]);
  
  return (
    <>
      {/* Major ports with persistent labels */}
      {majorPorts.map((pt, idx) => {
        const band = portWatchBand(pt.change_pct);
        const st = PORT_WATCH_STYLE[pt.trend][band];
        return (
          <React.Fragment key={`major-port-${pt.lat}-${pt.lng}-${idx}-${dataKey}`}>
            <CircleMarker
              center={[pt.lat, pt.lng]}
              radius={portWatchRadius(pt.change_pct)}
              pathOptions={{
                color: st.border,
                weight: 2,
                fillColor: st.bg,
                fillOpacity: 0.92,
              }}
            >
              <Tooltip direction="top" opacity={1} permanent>
                <div style={{
                  fontFamily: FONT,
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#111827",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  border: "1px solid #E5E7EB",
                  whiteSpace: "nowrap",
                  maxWidth: "150px",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>
                  {pt.location}
                </div>
              </Tooltip>
              <Tooltip direction="bottom" opacity={0} interactive>
                {renderPortWatchTooltip(pt, st)}
              </Tooltip>
            </CircleMarker>
          </React.Fragment>
        );
      })}
      
      {/* Other ports with clustering */}
      <MarkerClusterGroup
        ref={setClusterGroup}
        key={`portwatch-cluster-${dataKey}`}
      >
        {otherPorts.map((pt, idx) => {
          const band = portWatchBand(pt.change_pct);
          const st = PORT_WATCH_STYLE[pt.trend][band];
          return (
            <CircleMarker
              key={`pw-circle-${pt.lat}-${pt.lng}-${idx}-${pt.location}-${dataKey}`}
              center={[pt.lat, pt.lng]}
              radius={portWatchRadius(pt.change_pct)}
              pathOptions={{
                color: st.border,
                weight: 2,
                fillColor: st.bg,
                fillOpacity: 0.92,
              }}
            >
              <Tooltip direction="top" opacity={1} interactive>
                {renderPortWatchTooltip(pt, st)}
              </Tooltip>
            </CircleMarker>
          );
        })}
      </MarkerClusterGroup>
    </>
  );
};

const fmt = (v: number | null | undefined, unit = "") =>
  v != null && !Number.isNaN(v) ? `${Math.round(v)}${unit}` : "—";

function weatherAlertVisualEqual(a: WeatherAlert, b: WeatherAlert): boolean {
  return (
    a.id === b.id &&
    a.location === b.location &&
    a.lat === b.lat &&
    a.lng === b.lng &&
    a.severity === b.severity &&
    a.weather_description === b.weather_description &&
    a.temperature_f === b.temperature_f &&
    a.wind_speed_mph === b.wind_speed_mph &&
    a.wind_gust_mph === b.wind_gust_mph &&
    a.rain_mm === b.rain_mm &&
    a.snowfall_cm === b.snowfall_cm
  );
}

const WeatherMapMarker = React.memo(function WeatherMapMarkerFn({
  alert,
  wLat,
  wLng,
}: {
  alert: WeatherAlert;
  wLat: number;
  wLng: number;
}) {
  const position = useMemo(() => [wLat, wLng] as [number, number], [wLat, wLng]);
  const popupBody = useMemo(
    () => (
      <div style={{ fontFamily: FONT, padding: "4px 2px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827" }}>{alert.location}</span>
          <span
            style={{
              marginLeft: 8,
              padding: "2px 8px",
              borderRadius: 20,
              fontSize: "0.6875rem",
              fontWeight: 700,
              background: alert.severity === "high" ? "#FEF2F2" : "#F0FDF4",
              color: alert.severity === "high" ? WL_RED : "#16A34A",
              border: `1px solid ${alert.severity === "high" ? "#FECACA" : "#BBF7D0"}`,
              whiteSpace: "nowrap",
            }}
          >
            {alert.severity === "high" ? "⚠ Severe" : "☁ Low"}
          </span>
        </div>

        {alert.weather_description && (
          <p style={{ margin: "0 0 8px", fontSize: "0.8125rem", color: "#374151" }}>
            {formatWeatherType(alert.weather_description)}
          </p>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px" }}>
          {alert.temperature_f != null && (
            <div>
              <div style={{ fontSize: "0.6875rem", color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Temp
              </div>
              <div style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111827" }}>{fmt(alert.temperature_f)}°F</div>
            </div>
          )}
          {alert.wind_speed_mph != null && (
            <div>
              <div style={{ fontSize: "0.6875rem", color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Wind
              </div>
              <div style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111827" }}>{fmt(alert.wind_speed_mph)} mph</div>
            </div>
          )}
          {alert.wind_gust_mph != null && (
            <div>
              <div style={{ fontSize: "0.6875rem", color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Gusts
              </div>
              <div style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111827" }}>{fmt(alert.wind_gust_mph)} mph</div>
            </div>
          )}
          {(alert.rain_mm ?? 0) > 0 && (
            <div>
              <div style={{ fontSize: "0.6875rem", color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.04em" }}>Rain</div>
              <div style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111827" }}>{alert.rain_mm} mm</div>
            </div>
          )}
          {(alert.snowfall_cm ?? 0) > 0 && (
            <div>
              <div style={{ fontSize: "0.6875rem", color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.04em" }}>Snow</div>
              <div style={{ fontSize: "0.875rem", fontWeight: 700, color: "#2563EB" }}>{alert.snowfall_cm} cm</div>
            </div>
          )}
        </div>
      </div>
    ),
    [
      alert.location,
      alert.severity,
      alert.weather_description,
      alert.temperature_f,
      alert.wind_speed_mph,
      alert.wind_gust_mph,
      alert.rain_mm,
      alert.snowfall_cm,
    ],
  );

  return (
    <Marker position={position} icon={WEATHER_MARK_ICONS[alert.severity === "high" ? "high" : "low"]}>
      <Popup minWidth={220}>{popupBody}</Popup>
    </Marker>
  );
}, (prev, next) => prev.wLat === next.wLat && prev.wLng === next.wLng && weatherAlertVisualEqual(prev.alert, next.alert));

type PortSeverity = "critical" | "high" | "moderate";
const PORT_SEVERITIES: PortSeverity[] = ["critical", "high", "moderate"];

type PortTrendValue = "all" | "drop" | "increase";
const PORT_TREND_OPTIONS: ReadonlyArray<{ value: PortTrendValue; label: string }> = [
  { value: "all", label: "All Ports" },
  { value: "increase", label: "Congested" },
  { value: "drop", label: "Plunged" },
];

type WeatherSeverityValue = "all" | "high" | "low";
const WEATHER_SEVERITY_OPTIONS: ReadonlyArray<{ value: WeatherSeverityValue; label: string }> = [
  { value: "all", label: "All" },
  { value: "high", label: "Severe" },
  { value: "low", label: "Low" },
];

const formatWeatherType = (raw: string): string =>
  raw.replaceAll("_", " ").toLowerCase().replaceAll(/\b\w/g, (c) => c.toUpperCase());

interface TreeRowProps {
  depth: number;
  label: React.ReactNode;
  expandable?: boolean;
  expanded?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  trailing?: React.ReactNode;
}

// Pulled out of TreeRow's JSX to keep that component's cognitive complexity
// low — each helper handles one styling/branching concern.
const treeRowHoverBg = (disabled: boolean, selected: boolean): string => {
  if (disabled) return "transparent";
  return selected ? "rgba(220, 38, 38, 0.1)" : "#F9FAFB";
};

const treeRowLabelColor = (selected: boolean, depth: number): string => {
  if (selected) return WL_RED;
  return depth === 0 ? "#111827" : "#374151";
};

const treeRowLabelWeight = (selected: boolean, depth: number): number => {
  if (depth === 0 || selected) return 600;
  return 400;
};

const TreeRowIndicator: React.FC<{ expandable: boolean; expanded: boolean; selected: boolean }> = ({
  expandable,
  expanded,
  selected,
}) => {
  if (expandable) {
    return (
      <ExpandMoreIcon
        sx={{
          fontSize: 16,
          color: "#6B7280",
          transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
          transition: "transform 160ms",
        }}
      />
    );
  }
  if (selected) {
    return <CheckIcon sx={{ fontSize: 14, color: WL_RED }} />;
  }
  return null;
};

const TreeRow: React.FC<TreeRowProps> = ({
  depth,
  label,
  expandable = false,
  expanded = false,
  selected = false,
  disabled = false,
  onClick,
  trailing,
}) => {
  const interactive = Boolean(onClick) && !disabled;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!interactive || !onClick) return;
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    onClick();
  };

  return (
    <Box
      role={onClick ? "button" : undefined}
      tabIndex={interactive ? 0 : -1}
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        pl: 1 + depth * 1.75,
        pr: 1.25,
        py: 0.6,
        cursor: interactive ? "pointer" : "default",
        userSelect: "none",
        bgcolor: selected ? "rgba(220, 38, 38, 0.06)" : "transparent",
        borderLeft: selected ? `2px solid ${WL_RED}` : "2px solid transparent",
        opacity: disabled ? 0.5 : 1,
        transition: "background-color 120ms",
        "&:hover": { bgcolor: treeRowHoverBg(disabled, selected) },
      }}
    >
      <Box sx={{ width: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <TreeRowIndicator expandable={expandable} expanded={expanded} selected={selected} />
      </Box>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: depth === 0 ? "0.82rem" : "0.78rem",
          fontWeight: treeRowLabelWeight(selected, depth),
          color: treeRowLabelColor(selected, depth),
          flex: 1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
      {trailing && (
        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>{trailing}</Box>
      )}
    </Box>
  );
};

const GlobalShipmentMap: React.FC = () => {
  const [filter, setFilter] = useState<"all" | "high" | "low">("all");
  const [portTrendFilter, setPortTrendFilter] = useState<"all" | "drop" | "increase">("all");
  const [portSeverityFilter, setPortSeverityFilter] = useState<Set<PortSeverity>>(
    () => new Set(PORT_SEVERITIES),
  );
  const [weatherTypeFilter, setWeatherTypeFilter] = useState<Set<string>>(new Set());
  const [showPortStatus, setShowPortStatus] = useState(true);
  const [showWeatherAlerts, setShowWeatherAlerts] = useState(true);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => new Set<string>());
  const isExpanded = (nodeId: string) => expandedNodes.has(nodeId);
  const toggleNode = (nodeId: string) =>
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  const closeAllFilters = () => {
    setFilterAnchorEl(null);
  };
  const [alerts, setAlerts] = useState<WeatherAlert[]>([]);
  const [portWatchPoints, setPortWatchPoints] = useState<PortWatchPoint[]>([]);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const portWatchLoadingRef = useRef(true);
  const portWatchIsStaleRef = useRef(false);
  const [snapshotDate, setSnapshotDate] = useState<string | null>(null);

  // Inject CSS styles for weather icons
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = weatherIconStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      styleElement.remove();
    };
  }, []);

  useEffect(() => {
    setWeatherLoading(true);
    (apiService.getWeatherAlerts({ limit: 100 }) as Promise<{
      data: { alerts: WeatherAlert[]; count: number; snapshot_as_of_cst: string | null };
    }>)
      .then((res) => {
        setAlerts(res.data.alerts ?? []);
        setSnapshotDate(res.data.snapshot_as_of_cst ?? null);
      })
      .catch(() => {
        setAlerts([]);
        setSnapshotDate(null);
      })
      .finally(() => setWeatherLoading(false));
  }, []);

  useEffect(() => {
    let active = true;

    const parsePortWatchResponse = (res: { data: unknown }) => {
      const body =
        res.data && typeof res.data === "object"
          ? (res.data as Record<string, unknown>)
          : null;
      return {
        body,
        parsedPoints: extractPortWatchPoints(res.data),
        isStale: Boolean(body?.stale),
        cachedAt: typeof body?.cached_at === "string" ? body.cached_at : null,
      };
    };
    const waitBeforeRetry = async (attempt: number) =>
      new Promise((resolve) => setTimeout(resolve, 900 * attempt));

    const applyPortWatchState = (
      parsedPoints: PortWatchPoint[],
      isStale: boolean,
      cachedAt: string | null,
    ) => {
      setPortWatchPoints(parsedPoints);
      portWatchIsStaleRef.current = isStale;
    };

    const finishPortWatchLoading = () => {
      portWatchLoadingRef.current = false;
    };

    const handleStaleOrLiveResponse = async (
      attempt: number,
      parsedPoints: PortWatchPoint[],
      isStale: boolean,
      cachedAt: string | null,
    ): Promise<boolean> => {
      if (isStale) {
        // Always use the fresh data to ensure color consistency
        applyPortWatchState(parsedPoints, true, cachedAt);
        if (attempt < 3) {
          await waitBeforeRetry(attempt);
          return false;
        }
        finishPortWatchLoading();
        return true;
      }

      applyPortWatchState(parsedPoints, false, cachedAt);
      finishPortWatchLoading();
      return true;
    };

    const handleFetchError = async (attempt: number, error: unknown): Promise<boolean> => {
      console.warn("PortWatch fetch attempt failed", { attempt, error });
      if (attempt < 3) {
        await waitBeforeRetry(attempt);
        return false;
      }
      if (active) {
        // Keep last good points on transient backend/source failures.
        finishPortWatchLoading();
      }
      return true;
    };

    const fetchPortWatchWithRetry = async () => {
      portWatchLoadingRef.current = true;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const res = (await apiService.getPortWatchHeatmap({
            lookbackDays: 30,
          })) as { data: unknown };
          if (!active) return;
          const { parsedPoints, isStale, cachedAt } = parsePortWatchResponse(res);
          const done = await handleStaleOrLiveResponse(
            attempt,
            parsedPoints,
            isStale,
            cachedAt,
          );
          if (done) {
            return;
          }
        } catch (error) {
          const done = await handleFetchError(attempt, error);
          if (done) {
            return;
          }
        }
      }
    };

    fetchPortWatchWithRetry().catch((error) => {
      console.warn("PortWatch fetch failed unexpectedly", { error });
      if (active) {
        finishPortWatchLoading();
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const weatherTypes = useMemo(() => {
    const set = new Set<string>();
    alerts.forEach((a) => {
      if (a.weather_description) set.add(a.weather_description);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [alerts]);

  const visible = useMemo(
    () =>
      alerts
        .filter((a) => filter === "all" || a.severity === filter)
        .filter((a) =>
          weatherTypeFilter.size === 0 || weatherTypeFilter.has(a.weather_description),
        ),
    [alerts, filter, weatherTypeFilter],
  );
  const highCount = alerts.filter((a) => a.severity === "high").length;
  const lowCount  = alerts.filter((a) => a.severity === "low").length;
  const visiblePortWatchPoints = useMemo(
    () =>
      portWatchPoints
        .filter((p) => portWatchBand(p.change_pct) !== "watch")
        .filter((p) => portTrendFilter === "all" || p.trend === portTrendFilter)
        .filter((p) => {
          const band = portWatchBand(p.change_pct);
          if (band === "watch") return false;
          return portSeverityFilter.has(band as PortSeverity);
        }),
    [portWatchPoints, portTrendFilter, portSeverityFilter],
  );

  const activeFilterCount = useMemo(
    () =>
      (showPortStatus ? 0 : 1) +
      (showWeatherAlerts ? 0 : 1) +
      (portTrendFilter === "all" ? 0 : 1) +
      (portSeverityFilter.size === PORT_SEVERITIES.length ? 0 : 1) +
      (filter === "all" ? 0 : 1) +
      (weatherTypeFilter.size > 0 ? 1 : 0),
    [
      showPortStatus,
      showWeatherAlerts,
      portTrendFilter,
      portSeverityFilter,
      filter,
      weatherTypeFilter,
    ],
  );

  const filterSummary = (() => {
    if (activeFilterCount === 0) return "No filters";
    const suffix = activeFilterCount === 1 ? "" : "s";
    return `${activeFilterCount} filter${suffix}`;
  })();

  const togglePortSeverity = (sev: PortSeverity) => {
    setPortSeverityFilter((prev) => {
      const next = new Set(prev);
      if (next.has(sev)) next.delete(sev);
      else next.add(sev);
      // Don't allow empty — re-add if user tries to clear the last one
      if (next.size === 0) return prev;
      return next;
    });
  };

  const toggleWeatherType = (type: string) => {
    setWeatherTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const resetFilters = () => {
    setShowPortStatus(true);
    setShowWeatherAlerts(true);
    setPortTrendFilter("all");
    setPortSeverityFilter(new Set(PORT_SEVERITIES));
    setFilter("all");
    setWeatherTypeFilter(new Set());
  };

  const snapshotLabel = (() => {
    if (!snapshotDate) return null;
    // The backend column is already in CST — parse it directly to avoid JS
    // treating the naive datetime string as UTC or local time.
    // Expected format: "YYYY-MM-DD HH:MM:SS" or "YYYY-MM-DDTHH:MM:SS"
    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(snapshotDate);
    if (!m) return snapshotDate;
    const [, , month, day, hour24, min] = m;
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const monthName = months[Number(month) - 1] ?? month;
    const h = Number(hour24);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${monthName} ${Number(day)}, ${h12}:${min} ${ampm} CST`;
  })();
  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: "12px",
        border: `1px solid ${CARD_BORDER}`,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3, py: 2,
          borderBottom: "1px solid #EAEAEA",
          display: "flex",
          flexDirection: "column",
          gap: 0.35,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.25, flexWrap: "wrap" }}>
          <Box sx={{ flexShrink: 0 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: "0.98rem", color: "#111827", lineHeight: 1.1 }}>
              Weather &amp; Disruption Alerts
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.55,
              flexWrap: "wrap",
              flex: "1 1 auto",
              minWidth: 0,
              py: 0.25,
            }}
          >
            {/* Unified Filter Control */}
            <Badge
              badgeContent={activeFilterCount}
              color="error"
              overlap="circular"
              sx={{
                "& .MuiBadge-badge": {
                  fontFamily: FONT,
                  fontSize: "0.62rem",
                  height: 16,
                  minWidth: 16,
                  bgcolor: WL_RED,
                },
              }}
            >
              <Button
                size="small"
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
                sx={{
                  fontFamily: FONT,
                  fontSize: "0.78rem",
                  fontWeight: 500,
                  textTransform: "none",
                  color: "#374151",
                  bgcolor: "#FFFFFF",
                  border: "1px solid #EAEAEA",
                  borderRadius: "8px",
                  px: 1.25,
                  py: 0.5,
                  minHeight: 32,
                  "&:hover": { bgcolor: "#F9FAFB", borderColor: "#D1D5DB" },
                }}
              >
                Filters
              </Button>
            </Badge>
            <Popover
              open={Boolean(filterAnchorEl)}
              anchorEl={filterAnchorEl}
              onClose={closeAllFilters}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.75,
                    borderRadius: "12px",
                    border: `1px solid ${CARD_BORDER}`,
                    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
                    overflow: "visible",
                  },
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", width: 300, maxHeight: 480 }}>
                {/* Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.25,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: "0.85rem", color: "#111827" }}>
                    Filters
                  </Typography>
                  <Button
                    size="small"
                    onClick={resetFilters}
                    startIcon={<RestartAltIcon sx={{ fontSize: 14 }} />}
                    disabled={activeFilterCount === 0}
                    sx={{
                      fontFamily: FONT,
                      fontSize: "0.72rem",
                      textTransform: "none",
                      color: WL_RED,
                      "&.Mui-disabled": { color: "#9CA3AF" },
                    }}
                  >
                    Reset all
                  </Button>
                </Box>

                {/* Tree body */}
                <Box sx={{ flex: 1, overflowY: "auto", py: 0.5 }}>
                  {/* Map Layers (root) — Port Status & Weather Alerts nest under here */}
                  <TreeRow
                    depth={0}
                    label="Map Layers"
                    expandable
                    expanded={isExpanded("layers")}
                    onClick={() => toggleNode("layers")}
                    trailing={
                      <Typography sx={{ fontFamily: FONT, fontSize: "0.7rem", color: "#9CA3AF" }}>
                        {(showPortStatus ? 1 : 0) + (showWeatherAlerts ? 1 : 0)}/2
                      </Typography>
                    }
                  />
                  {isExpanded("layers") && (
                    <>
                      {/* Port Status layer */}
                      <TreeRow
                        depth={1}
                        label="Port Status"
                        expandable
                        expanded={isExpanded("layers.port")}
                        onClick={() => toggleNode("layers.port")}
                        trailing={
                          <Switch
                            size="small"
                            checked={showPortStatus}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setShowPortStatus(e.target.checked)}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": { color: WL_RED },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                bgcolor: WL_RED,
                              },
                            }}
                          />
                        }
                      />
                      {isExpanded("layers.port") && (
                        <Box
                          sx={{
                            opacity: showPortStatus ? 1 : 0.5,
                            pointerEvents: showPortStatus ? "auto" : "none",
                          }}
                        >
                          <TreeRow
                            depth={2}
                            label="Trend"
                            expandable
                            expanded={isExpanded("layers.port.trend")}
                            onClick={() => toggleNode("layers.port.trend")}
                          />
                          {isExpanded("layers.port.trend") &&
                            PORT_TREND_OPTIONS.map((opt) => (
                              <TreeRow
                                key={opt.value}
                                depth={3}
                                label={opt.label}
                                selected={portTrendFilter === opt.value}
                                onClick={() => setPortTrendFilter(opt.value)}
                              />
                            ))}
                          <TreeRow
                            depth={2}
                            label="Severity"
                            expandable
                            expanded={isExpanded("layers.port.severity")}
                            onClick={() => toggleNode("layers.port.severity")}
                            trailing={
                              <Typography sx={{ fontFamily: FONT, fontSize: "0.7rem", color: "#9CA3AF" }}>
                                {portSeverityFilter.size}/{PORT_SEVERITIES.length}
                              </Typography>
                            }
                          />
                          {isExpanded("layers.port.severity") &&
                            PORT_SEVERITIES.map((sev) => (
                              <TreeRow
                                key={sev}
                                depth={3}
                                label={sev.charAt(0).toUpperCase() + sev.slice(1)}
                                selected={portSeverityFilter.has(sev)}
                                onClick={() => togglePortSeverity(sev)}
                              />
                            ))}
                        </Box>
                      )}

                      {/* Weather Alerts layer */}
                      <TreeRow
                        depth={1}
                        label="Weather Alerts"
                        expandable
                        expanded={isExpanded("layers.weather")}
                        onClick={() => toggleNode("layers.weather")}
                        trailing={
                          <Switch
                            size="small"
                            checked={showWeatherAlerts}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setShowWeatherAlerts(e.target.checked)}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": { color: WL_RED },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                bgcolor: WL_RED,
                              },
                            }}
                          />
                        }
                      />
                      {isExpanded("layers.weather") && (
                        <Box
                          sx={{
                            opacity: showWeatherAlerts ? 1 : 0.5,
                            pointerEvents: showWeatherAlerts ? "auto" : "none",
                          }}
                        >
                          <TreeRow
                            depth={2}
                            label="Severity"
                            expandable
                            expanded={isExpanded("layers.weather.severity")}
                            onClick={() => toggleNode("layers.weather.severity")}
                          />
                          {isExpanded("layers.weather.severity") &&
                            WEATHER_SEVERITY_OPTIONS.map((opt) => {
                              const severityCounts: Record<WeatherSeverityValue, number> = {
                                all: alerts.length,
                                high: highCount,
                                low: lowCount,
                              };
                              const count = severityCounts[opt.value];
                              return (
                                <TreeRow
                                  key={opt.value}
                                  depth={3}
                                  label={`${opt.label} (${count})`}
                                  selected={filter === opt.value}
                                  onClick={() => setFilter(opt.value)}
                                />
                              );
                            })}
                          <TreeRow
                            depth={2}
                            label="Alert Type"
                            expandable
                            expanded={isExpanded("layers.weather.type")}
                            onClick={() => toggleNode("layers.weather.type")}
                            trailing={
                              weatherTypeFilter.size > 0 ? (
                                <Typography
                                  component="span"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setWeatherTypeFilter(new Set());
                                  }}
                                  sx={{
                                    fontFamily: FONT,
                                    fontSize: "0.68rem",
                                    color: WL_RED,
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  Clear ({weatherTypeFilter.size})
                                </Typography>
                              ) : null
                            }
                          />
                          {isExpanded("layers.weather.type") &&
                            (weatherTypes.length === 0 ? (
                              <Typography
                                sx={{
                                  fontFamily: FONT,
                                  fontSize: "0.72rem",
                                  color: "#9CA3AF",
                                  pl: 1 + 3 * 1.75,
                                  py: 0.5,
                                }}
                              >
                                No alert types available.
                              </Typography>
                            ) : (
                              weatherTypes.map((type) => (
                                <TreeRow
                                  key={type}
                                  depth={3}
                                  label={formatWeatherType(type)}
                                  selected={weatherTypeFilter.has(type)}
                                  onClick={() => toggleWeatherType(type)}
                                />
                              ))
                            ))}
                        </Box>
                      )}
                    </>
                  )}
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.25,
                    borderTop: "1px solid #F1F5F9",
                  }}
                >
                  <Typography sx={{ fontFamily: FONT, fontSize: "0.72rem", color: "#6B7280" }}>
                    {filterSummary}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    onClick={closeAllFilters}
                    sx={{
                      fontFamily: FONT,
                      fontSize: "0.78rem",
                      fontWeight: 500,
                      textTransform: "none",
                      bgcolor: WL_RED,
                      color: "#FFFFFF",
                      borderRadius: "8px",
                      px: 2,
                      py: 0.5,
                      minHeight: 30,
                      "&:hover": { bgcolor: "#B91C1C" },
                    }}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            </Popover>
          </Box>
        </Box>
        {snapshotLabel && (
          <Typography sx={{ fontFamily: FONT, fontSize: "0.75rem", color: "#9CA3AF", mt: 0.1 }}>
            Updated on {snapshotLabel}
          </Typography>
        )}
      </Box>

      {/* Map */}
      <Box sx={{ height: 350, position: "relative", flexShrink: 0 }}>
        {weatherLoading && (
          <Box sx={{
            position: "absolute", inset: 0, zIndex: 900,
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.75)", backdropFilter: "blur(2px)",
          }}>
            <CircularProgress size={32} sx={{ color: WL_RED }} />
          </Box>
        )}

        <MapContainer
          center={[25, 10]}
          zoom={2}
          minZoom={2}
          worldCopyJump={false}
          style={{ height: 350, width: "100%" }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {showPortStatus && <PortWatchCircleLayer points={visiblePortWatchPoints} />}

          {showWeatherAlerts && visible.map((alert, idx) => {
            const [wLat, wLng] = nudgedWeatherLatLng(alert.lat, alert.lng, visiblePortWatchPoints, idx);
            return <WeatherMapMarker key={alert.id} alert={alert} wLat={wLat} wLng={wLng} />;
          })}
        </MapContainer>

      </Box>

      {/* Legend */}
      <Box
        sx={{
          px: 3,
          py: 1.25,
          borderTop: "1px solid #EAEAEA",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "nowrap",
          gap: 2,
          overflowX: "auto",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, px: 1.5, py: 0.9, borderRadius: "8px", bgcolor: "#F8FAFC", border: "1px solid #E5E7EB", whiteSpace: "nowrap" }}>
          <Typography sx={{ fontFamily: FONT, fontSize: "0.9rem", color: "#111827", fontWeight: 700 }}>
            PortWatch Markers
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <span style={{ width: 11, height: 11, borderRadius: "50%", background: PORT_WATCH_STYLE.drop.critical.bg, border: `1px solid ${PORT_WATCH_STYLE.drop.critical.border}` }} />
            <Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", color: "#4B5563" }}>Traffic Drop</Typography>
          </Box>
          <Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", color: "#9CA3AF", mx: 0.2 }}>|</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 0.2 }}>
            <span style={{ width: 11, height: 11, borderRadius: "50%", background: PORT_WATCH_STYLE.increase.critical.bg, border: `1px solid ${PORT_WATCH_STYLE.increase.critical.border}` }} />
            <Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", color: "#4B5563" }}>Traffic Increase</Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, px: 1.5, py: 0.9, borderRadius: "8px", bgcolor: "#F8FAFC", border: "1px solid #E5E7EB", whiteSpace: "nowrap" }}>
          <Typography sx={{ fontFamily: FONT, fontSize: "0.9rem", color: "#111827", fontWeight: 700 }}>
            Weather Risk
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <div style={{ width: 11, height: 11, borderRadius: "50%", background: WL_RED }} />
            <Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", color: "#4B5563" }}>High: {highCount}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <div style={{ width: 11, height: 11, borderRadius: "50%", background: "#16A34A" }} />
            <Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", color: "#4B5563" }}>Low: {lowCount}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GlobalShipmentMap;
